<script lang="ts">
  /**
   * Fastest25sSheet — "Fastest 25s · <Stroke>" (Scott, 2026-09-11: "the best 25
   * should be clickable"). Opens from a stroke card's Best 25 tile via
   * { kind: "fastest-25s", stroke }.
   *
   * One row per 25 m swim: that swim's fastest length of the stroke, read from
   * the swimFacts cache (no fetch, no schema change), fastest first. The same
   * record floor as the tile, so the top row IS the tile's number and a
   * split-length glitch never ranks. Row = date + time; tap opens the swim.
   *
   * The count names its subset: "87 of 92 swims" when some of the stroke card's
   * swims (swims25) have no length past the floor, so it never reads as a
   * different number for the same thing (VP panel round 2, 2026-09-11).
   */
  import { closeOverlay } from "../lib/overlay";
  import { lockBodyScroll, unlockBodyScroll } from "../lib/native/bodyScrollLock";
  import { sheetDrag } from "../lib/sheetDrag";
  import { getAllSwimFacts } from "../lib/allTimeAggregates";
  import { fastest25s, swims25 } from "../lib/strokeInsights";
  import { fmtSetDate } from "../lib/setHistory";
  import { fmtSeconds1 } from "../lib/format";
  import { strokeColor } from "../lib/designTokens";
  import { goToTab } from "../lib/tabNav";
  import { haptic } from "../lib/haptics";

  interface Props {
    open?: boolean;
    stroke: string;
  }
  let { open = false, stroke }: Props = $props();

  function close() {
    closeOverlay("fastest-25s");
  }

  function onKey(e: KeyboardEvent) {
    // Only while open: OverlayHost keeps the sheet mounted.
    if (open && e.key === "Escape") close();
  }

  /** Re-read from the cache each time the sheet opens. */
  let facts = $derived(open && stroke ? getAllSwimFacts() : []);
  let rows = $derived(fastest25s(facts, stroke));
  /** The stroke card's "N swims": every 25 m swim of the stroke, ranked or not. */
  let swimsOfStroke = $derived(swims25(facts, stroke).length);

  function openSwim(workoutId: string) {
    haptic("light");
    close();
    goToTab("swim", { workoutUUID: workoutId });
  }

  $effect(() => {
    if (!open) return;
    lockBodyScroll();
    return () => unlockBodyScroll();
  });
</script>

<svelte:window onkeydown={onKey} />

{#if open && stroke}
  <div
    class="scrim"
    role="presentation"
    onclick={close}
    onkeydown={(e) => e.key === "Enter" && close()}
  ></div>
  <div class="sheet" role="dialog" aria-modal="true" aria-label={`Fastest 25s · ${stroke}`} use:sheetDrag={close}>
    <div class="grabber" aria-hidden="true"></div>
    <div class="content">
      <header class="hdr">
        <span></span>
        <button type="button" class="pill" onclick={close}>Done</button>
      </header>

      <h1 class="title">
        <span class="dot" style:background={strokeColor(stroke)} aria-hidden="true"></span>Fastest 25s · {stroke}
      </h1>
      <p class="meta">
        {rows.length < swimsOfStroke ? `${rows.length} of ${swimsOfStroke} swims` : rows.length === 1 ? "1 swim" : `${rows.length} swims`} · one length each
      </p>

      {#if rows.length === 0}
        <p class="zero">No 25 m length of {stroke.toLowerCase()} yet.</p>
      {:else}
        <ol class="list">
          {#each rows as r, i (r.workoutId)}
            <li>
              <button
                type="button"
                class="row"
                data-fastest-row={r.workoutId}
                aria-label={`${i + 1}. ${fmtSetDate(r.dateMs)}, ${fmtSeconds1(r.seconds)} seconds. Open the swim.`}
                onclick={() => openSwim(r.workoutId)}
              >
                <span class="date">{fmtSetDate(r.dateMs)}</span>
                <span class="time">{fmtSeconds1(r.seconds)}<span class="u">s</span></span>
                <span class="chev" aria-hidden="true">›</span>
              </button>
            </li>
          {/each}
        </ol>
      {/if}
    </div>
  </div>
{/if}

<style>
  .scrim {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 50;
    animation: scrim-in var(--dur-card) var(--ease-swim);
    touch-action: none;
  }
  .sheet {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 51;
    max-height: 85vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    background: var(--bg-canvas);
    border-radius: 20px 20px 0 0;
    animation: sheet-in var(--dur-card) var(--ease-swim);
  }
  /* The other sheets' grabber: the cue that a drag down closes it. */
  .grabber {
    position: absolute;
    top: 8px;
    left: 50%;
    width: 36px;
    height: 4px;
    margin-left: -18px;
    border-radius: 999px;
    background: var(--divider-strong);
  }
  .content {
    max-width: 430px;
    margin: 0 auto;
    padding: var(--space-5) var(--space-4) calc(var(--space-6) + env(safe-area-inset-bottom, 0px));
    display: flex;
    flex-direction: column;
  }
  .hdr {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    margin-bottom: var(--space-3);
  }
  /* Big, obvious controls: 17px floor, wet-hands target. */
  .pill {
    font-size: 17px;
    font-weight: 600;
    color: var(--accent-primary);
    background: var(--bg-raised);
    border: none;
    border-radius: 12px;
    padding: 10px 16px;
    min-height: 44px;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .title {
    margin: 0;
    font-size: 28px;
    font-weight: 700;
    letter-spacing: -0.3px;
    color: var(--text-primary);
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .dot {
    width: 12px;
    height: 12px;
    border-radius: 999px;
    flex-shrink: 0;
  }
  .meta {
    margin: 2px 0 var(--space-3);
    font-size: 15px;
    color: var(--text-muted);
    font-variant-numeric: tabular-nums;
  }
  .zero {
    margin: var(--space-4) 0 0;
    font-size: 17px;
    color: var(--text-muted);
  }

  .list {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .row {
    width: 100%;
    min-height: 48px;
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: 0 2px;
    border: 0;
    border-bottom: 1px solid var(--divider);
    background: transparent;
    color: var(--text-body);
    font-family: var(--font-text);
    text-align: left;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .row:active {
    background: var(--bg-raised);
  }
  .row:focus-visible {
    outline: 2px solid var(--accent-yellow);
    outline-offset: -2px;
  }
  .date {
    flex: 1;
    font-size: 17px;
  }
  .time {
    font-size: 20px;
    font-weight: 600;
    color: var(--text-primary);
    font-variant-numeric: tabular-nums;
  }
  .u {
    font-size: 14px;
    font-weight: 500;
    color: var(--text-body);
    margin-left: 1px;
  }
  .chev {
    font-size: 20px;
    color: var(--text-muted);
  }
</style>
